import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/user/slice';
import {
  Content,
  NoteText,
  PageContainer,
  StatisticContainer,
  TitleContainer,
  TitleContent,
} from './WalletPage.styles';
import { Text16Light, Text24Bold } from '../../components/utils/Text/Text.styles';
import {
  CreateMassPayUserModal,
  MassPayNoteModal,
  RedirectingModal,
  RequestWithdrawalButton,
  StatisticCard,
  Table,
  WithdrawalItem,
} from '../../components';
import { ReactComponent as WalletIcon } from '../../assets/icons/wallet.svg';
import { ReactComponent as SafeIcon } from '../../assets/icons/safe.svg';
import { ReactComponent as CreditCardIcon } from '../../assets/icons/credit-card-down.svg';
import { selectFetchWithdrawalsInfo, selectWithdrawalActionsPending } from '../../store/slices/withdrawal/slice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchMassPayActivationUrl, fetchWithdrawals } from '../../store/slices/withdrawal/asyncThunks';
import { notifyError } from '../../utils/notify';
import { WITHDRAW } from '../../components/App/routes';
import SupportModal from '../../components/modals/SupportModal/SupportModal';

const WalletPage = () => {
  const navigate = useNavigate();
  const createMassPayModalRef = useRef();
  const massPayNoteModalRef = useRef();
  const redirectingModalRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = +searchParams.get('page') || 1;
  const [showSupport, setShowSupport] = useState(false);

  const user = useSelector(selectUser);
  const { data, total, pending } = useSelector(selectFetchWithdrawalsInfo);
  const { fetchMassPayActivationUrl: fetchMassPayActivationUrlPending } = useSelector(selectWithdrawalActionsPending);
  const { earnings } = user.analytics;

  const dispatch = useDispatch();

  useEffect(() => {
    if (page > 0) {
      dispatch(fetchWithdrawals({ page }));
    }
  }, [dispatch, page]);

  const setPage = page => {
    searchParams.set('page', page);
    setSearchParams(searchParams);
  };

  const tableHeader = [{ label: 'Created At' }, { label: 'Amount' }, { label: 'Status' }, {}];

  const onRequestClick = () => {
    if (user.massPay.status) {
      dispatch(fetchMassPayActivationUrl())
        .unwrap()
        .then(({ status, activationUrl }) => {
          if (status === 'ACTIVE' && !activationUrl) {
            navigate(`/${WITHDRAW}`);
          } else if (activationUrl) {
            redirectingModalRef.current.show();
            setTimeout(() => {
              window.location.href = activationUrl;
              setTimeout(() => {
                redirectingModalRef.current.hide();
              }, 2000);
            }, 500);
          } else {
            setShowSupport(true);
          }
        })
        .catch(err => {
          notifyError(err.message);
        });
    } else {
      massPayNoteModalRef.current.show();
    }
  };

  const isMassPayActive = user.massPay?.status === 'ACTIVE';

  return (
    <>
      <PageContainer>
        <TitleContainer>
          <TitleContent>
            <Text24Bold>Wallet</Text24Bold>
            {user.massPay?.email && <Text16Light>Mass Pay email: {user.massPay.email}</Text16Light>}
          </TitleContent>
          <RequestWithdrawalButton
            disabled={earnings.available < 500 || user.thisMonthWithdrawals >= 2 || user.hasPendingWithdrawal}
            title="Request Withdrawal"
            onClick={onRequestClick}
            loading={fetchMassPayActivationUrlPending}
          />
        </TitleContainer>
        <Content>
          <NoteText>
            Payments take approximately 5–7 business days to be processed and credited to your MassPay account after
            they are requested.
          </NoteText>
          <StatisticContainer>
            <StatisticCard title="Available Balance" icon={<CreditCardIcon />} value={earnings.available} hideGross />
            <StatisticCard
              title="Pending Balance"
              icon={<WalletIcon />}
              value={earnings.balance - earnings.available}
              hideGross
            />
            <StatisticCard
              title="Lifetime Proceeds"
              icon={<SafeIcon />}
              value={earnings.lifetime.net}
              gross={earnings.lifetime.gross}
            />
          </StatisticContainer>
          <Table
            headerInformation={tableHeader}
            dataLoading={pending}
            totalCount={total}
            currentPage={page}
            perPage={10}
            setPage={setPage}
            emptyDataText={'No data'}
            fullHeight>
            {data.map(withdrawal => (
              <WithdrawalItem
                key={withdrawal._id}
                status={withdrawal.status}
                amount={withdrawal.amount}
                createdAt={withdrawal.createdAt}
                onGoToWallet={onRequestClick}
                massPayLoginUrl={isMassPayActive ? 'https://members.masspay.io/v2/login' : undefined}
              />
            ))}
          </Table>
        </Content>
      </PageContainer>
      <CreateMassPayUserModal modalRef={createMassPayModalRef} />
      <SupportModal show={showSupport} setShow={setShowSupport} />
      <RedirectingModal modalRef={redirectingModalRef} />
      <MassPayNoteModal modalRef={massPayNoteModalRef} createMassPayModalRef={createMassPayModalRef} />
    </>
  );
};

export default WalletPage;
